import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// =====================================================
// Types
// =====================================================

export interface TemplateInfo {
  id: string;
  name: string;
  recurrence_type: string;
  days_pattern: any;
}

export interface EmployeeWithTemplate {
  id: string;
  first_name: string;
  last_name: string;
  middle_name?: string | null;
  position: { id: string; name: string } | null;
  department_id: string | null;
  current_template: TemplateInfo | null;
}

export interface TemplateSearchResult {
  id: string;
  name: string;
  recurrence_type: string;
  days_pattern: any;
  work_days: number;
  off_days: number;
  shift_start: string | null;
  shift_end: string | null;
  match_score: number;
}

export interface ChangeRequestItem {
  id: string;
  employee: {
    id: string;
    first_name: string;
    last_name: string;
    middle_name?: string | null;
    position: { id: string; name: string } | null;
  };
  current_template: TemplateInfo | null;
  new_template: TemplateInfo | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_at: string | null;
}

export interface ChangeRequest {
  id: string;
  department: { id: string; name: string };
  requested_by: { id: string; first_name: string; last_name: string };
  reviewed_by?: { id: string; first_name: string; last_name: string } | null;
  year: number;
  month: number;
  valid_from: string;
  valid_until: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  comment: string | null;
  created_at: string;
  reviewed_at: string | null;
  items?: ChangeRequestItem[];
}

export interface ChangeRequestListItem {
  id: string;
  department: {
    id: string;
    name: string;
    organization?: { id: string; name: string } | null;
  };
  requested_by?: { id: string; first_name: string; last_name: string };
  year: number;
  month: number;
  valid_from: string;
  valid_until: string;
  status: string;
  items_count: number;
  comment: string | null;
  created_at: string;
  reviewed_at: string | null;
}

export interface ManagedDepartment {
  id: string;
  name: string;
}

// =====================================================
// Query Keys
// =====================================================

const KEYS = {
  all: ['schedule-change-requests'] as const,
  lists: () => [...KEYS.all, 'list'] as const,
  myRequests: () => [...KEYS.all, 'my'] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
  myEmployees: (deptId?: string) =>
    [...KEYS.all, 'my-employees', deptId] as const,
  myDepartments: () => [...KEYS.all, 'my-departments'] as const,
  templateSearch: (params: any) =>
    [...KEYS.all, 'templates-search', params] as const,
};

// =====================================================
// Manager Hooks
// =====================================================

export const useManagedDepartments = () => {
  return useQuery<ManagedDepartment[]>({
    queryKey: KEYS.myDepartments(),
    queryFn: async () => {
      return api.get('/api/v1/schedule-change-requests/my-departments');
    },
  });
};

export const useMyEmployees = (departmentId?: string) => {
  return useQuery<EmployeeWithTemplate[]>({
    queryKey: KEYS.myEmployees(departmentId),
    queryFn: async () => {
      return api.get('/api/v1/schedule-change-requests/my-employees', {
        params: { departmentId },
      });
    },
    enabled: !!departmentId,
  });
};

export interface TemplateSearchParams {
  search?: string;
  workDays?: number;
  offDays?: number;
  shiftStart?: string;
  shiftEnd?: string;
}

export const useTemplateSearch = (params: TemplateSearchParams) => {
  return useQuery<TemplateSearchResult[]>({
    queryKey: KEYS.templateSearch(params),
    queryFn: async () => {
      return api.get('/api/v1/schedule-change-requests/templates/search', {
        params,
      });
    },
  });
};

export const useMyRequests = () => {
  return useQuery<ChangeRequestListItem[]>({
    queryKey: KEYS.myRequests(),
    queryFn: async () => {
      return api.get('/api/v1/schedule-change-requests/my');
    },
  });
};

export const useCreateChangeRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      department_id: string;
      valid_from: string;
      valid_until: string;
      items: { employee_id: string; new_template_id: string }[];
    }) => {
      return api.post('/api/v1/schedule-change-requests', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

export const useCancelRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId: string) => {
      return api.patch(
        `/api/v1/schedule-change-requests/${requestId}/cancel`,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

// =====================================================
// HR Hooks
// =====================================================

export interface RequestFilterParams {
  status?: string;
  department_id?: string;
  organization_id?: string;
  year?: number;
  month?: number;
}

export const useAllRequests = (filter: RequestFilterParams = {}) => {
  return useQuery<ChangeRequestListItem[]>({
    queryKey: [...KEYS.lists(), filter],
    queryFn: async () => {
      return api.get('/api/v1/schedule-change-requests', {
        params: filter,
      });
    },
  });
};

export const usePendingRequestsCount = () => {
  return useQuery<number>({
    queryKey: [...KEYS.lists(), 'pending-count'],
    queryFn: async () => {
      const list: ChangeRequestListItem[] = await api.get(
        '/api/v1/schedule-change-requests',
        { params: { status: 'pending' } },
      );
      return list.length;
    },
    refetchInterval: 60_000,
  });
};

export const useChangeRequest = (id: string | null) => {
  return useQuery<ChangeRequest>({
    queryKey: KEYS.detail(id || ''),
    queryFn: async () => {
      if (!id) throw new Error('ID is required');
      return api.get(`/api/v1/schedule-change-requests/${id}`);
    },
    enabled: !!id,
  });
};

export const useReviewRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      requestId,
      data,
    }: {
      requestId: string;
      data: {
        items: { item_id: string; status: 'approved' | 'rejected' }[];
        comment?: string;
      };
    }) => {
      return api.patch(
        `/api/v1/schedule-change-requests/${requestId}/review`,
        data,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};
